import React, { useState, useEffect } from 'react';
import { apiKeysAPI } from '../api';

function APIKeys() {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [keyName, setKeyName] = useState('');
  const [expiresIn, setExpiresIn] = useState('');
  const [newApiKey, setNewApiKey] = useState(null);
  const [message, setMessage] = useState('');

  useEffect(() => {
    loadKeys();
  }, []);

  const loadKeys = async () => {
    setLoading(true);
    try {
      const response = await apiKeysAPI.list();
      setKeys(response.data.keys);
    } catch (error) {
      console.error('Error loading API keys:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateKey = async (e) => {
    e.preventDefault();
    setMessage('');
    setNewApiKey(null);

    try {
      const expires_days = expiresIn ? parseInt(expiresIn) : null;
      const response = await apiKeysAPI.create(keyName, expires_days);
      
      if (response.data.success) {
        setNewApiKey(response.data.api_key);
        setKeyName('');
        setExpiresIn('');
        setShowCreateForm(false);
        loadKeys();
      }
    } catch (error) {
      setMessage(error.response?.data?.error || 'Failed to create API key');
    }
  };

  const handleDeleteKey = async (keyId) => {
    if (!window.confirm('Are you sure you want to delete this API key?')) return;

    try {
      await apiKeysAPI.delete(keyId);
      loadKeys();
    } catch (error) {
      console.error('Error deleting API key:', error);
    }
  };

  const handleRevokeKey = async (keyId) => {
    if (!window.confirm('Are you sure you want to revoke this API key?')) return;

    try {
      await apiKeysAPI.revoke(keyId);
      loadKeys();
    } catch (error) {
      console.error('Error revoking API key:', error);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert('API key copied to clipboard!');
  };

  return (
    <div className="container">
      <h1 className="mb-3">API Keys</h1>

      {/* New API Key Display */}
      {newApiKey && (
        <div
          className="card mb-3"
          style={{
            background: 'var(--bg-dark)',
            borderColor: 'var(--accent-success)',
            boxShadow: '0 4px 16px var(--shadow-medium)'
          }}
        >
          <h3 className="mb-2" style={{ color: 'var(--accent-success)' }}>API Key Created!</h3>
          <p style={{ color: 'var(--text-secondary)' }}>
            <strong>Important:</strong> Copy this key now. You won't be able to see it again!
          </p>
          <div className="d-flex gap-2 align-center">
            <code
              style={{
                flex: 1,
                padding: '10px',
                background: 'var(--bg-darkest)',
                fontSize: '14px',
                wordBreak: 'break-all',
                border: '1px solid var(--border-color)',
                borderRadius: '4px'
              }}
            >
              {newApiKey}
            </code>
            <button
              onClick={() => copyToClipboard(newApiKey)}
              className="btn btn-success"
            >
              Copy
            </button>
          </div>
        </div>
      )}

      {/* Create Button */}
      <div className="card mb-3">
        <div className="d-flex justify-between align-center">
          <h3>Your API Keys</h3>
          <button
            onClick={() => setShowCreateForm(!showCreateForm)}
            className="btn btn-primary"
          >
            {showCreateForm ? 'Cancel' : 'Create New Key'}
          </button>
        </div>

        {/* Create Form */}
        {showCreateForm && (
          <form onSubmit={handleCreateKey} className="mt-3">
            {message && <div className="alert alert-error">{message}</div>}
            
            <div className="form-group">
              <label className="form-label">Key Name</label>
              <input
                type="text"
                className="form-control"
                value={keyName}
                onChange={(e) => setKeyName(e.target.value)}
                placeholder="e.g., Production API, Mobile App"
                required
              />
            </div>
            
            <div className="form-group">
              <label className="form-label">Expires In (days)</label>
              <input
                type="number"
                className="form-control"
                value={expiresIn}
                onChange={(e) => setExpiresIn(e.target.value)}
                placeholder="Leave empty for no expiration"
                min="1"
              />
            </div>
            
            <button type="submit" className="btn btn-success">
              Create API Key
            </button>
          </form>
        )}
      </div>

      {/* API Keys List */}
      <div className="card">
        {loading ? (
          <div className="loading">
            <div className="spinner"></div>
          </div>
        ) : keys.length === 0 ? (
          <p className="text-muted">No API keys created yet.</p>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Created</th>
                <th>Expires</th>
                <th>Last Used</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {keys.map((key) => (
                <tr key={key.key_id}>
                  <td>
                    <strong>{key.name}</strong>
                    <br />
                    <span className="text-small text-muted">
                      ID: {key.key_id.substring(0, 12)}...
                    </span>
                  </td>
                  <td>{formatDate(key.created_at)}</td>
                  <td>{formatDate(key.expires_at)}</td>
                  <td>{formatDate(key.last_used)}</td>
                  <td>
                    <span
                      className={`badge ${
                        key.active ? 'badge-success' : 'badge-danger'
                      }`}
                    >
                      {key.active ? 'Active' : 'Revoked'}
                    </span>
                  </td>
                  <td>
                    <div className="d-flex gap-1">
                      {key.active && (
                        <button
                          onClick={() => handleRevokeKey(key.key_id)}
                          className="btn btn-secondary"
                          style={{ padding: '6px 12px', fontSize: '12px' }}
                        >
                          Revoke
                        </button>
                      )}
                      <button
                        onClick={() => handleDeleteKey(key.key_id)}
                        className="btn btn-danger"
                        style={{ padding: '6px 12px', fontSize: '12px' }}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Usage Instructions */}
      <div className="card mt-3">
        <h3 className="mb-2">Using API Keys</h3>
        <p className="text-muted">
          Include your API key in the request headers:
        </p>
        <code
          style={{
            display: 'block',
            padding: '10px',
            background: 'var(--bg-darkest)',
            fontSize: '14px',
            border: '1px solid var(--border-color)',
            borderRadius: '4px'
          }}
        >
          X-API-Key: your_api_key_here
        </code>
      </div>

      {/* Code Examples */}
      <div className="card mt-3">
        <h3 className="mb-3">Upload File Examples</h3>
        
        {/* cURL Example */}
        <div className="mb-3">
          <h4 className="mb-2">cURL</h4>
          <pre
            style={{
              background: 'var(--bg-dark)',
              color: 'var(--text-primary)',
              padding: '15px',
              overflow: 'auto',
              fontSize: '13px',
              lineHeight: '1.5',
              border: '1px solid var(--border-color)',
              borderRadius: '4px'
            }}
          >
{`curl -X POST http://localhost:5001/api/files/upload \\
  -H "X-API-Key: your_api_key_here" \\
  -F "file=@/path/to/your/file.apk" \\
  -F "is_public=false"

# Response:
# {
#   "success": true,
#   "message": "File uploaded successfully",
#   "file_id": "abc123..."
# }`}
          </pre>
        </div>

        {/* Python Example */}
        <div className="mb-3">
          <h4 className="mb-2">Python</h4>
          <pre
            style={{
              background: 'var(--bg-dark)',
              color: 'var(--text-primary)',
              padding: '15px',
              overflow: 'auto',
              fontSize: '13px',
              lineHeight: '1.5',
              border: '1px solid var(--border-color)',
              borderRadius: '4px'
            }}
          >
{`import requests

# Configuration
API_URL = "http://localhost:5001/api/files/upload"
API_KEY = "your_api_key_here"
FILE_PATH = "/path/to/your/file.apk"

# Upload file
with open(FILE_PATH, 'rb') as f:
    files = {'file': f}
    data = {'is_public': 'false'}
    headers = {'X-API-Key': API_KEY}
    
    response = requests.post(API_URL, files=files, data=data, headers=headers)
    
    if response.status_code == 200:
        result = response.json()
        print(f"Success! File ID: {result['file_id']}")
    else:
        print(f"Error: {response.json()}")`}
          </pre>
        </div>

        {/* Additional Notes */}
        <div
          className="mt-3"
          style={{
            background: 'var(--bg-mid)',
            padding: '12px',
            border: '1px solid var(--border-color)',
            borderRadius: '4px'
          }}
        >
          <h4 className="mb-2" style={{ fontSize: '14px', fontWeight: 'bold' }}>Notes:</h4>
          <ul style={{ marginBottom: 0, paddingLeft: '20px', fontSize: '13px' }}>
            <li><code>is_public</code> parameter: Set to <code>true</code> to make the file publicly accessible, <code>false</code> for private files</li>
            <li>Max upload size: 2GB</li>
            <li>The API will return a <code>file_id</code> that can be used to manage or download the file</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

export default APIKeys;
